/**
 * Subscription plan picker.
 *
 * Behaviour model ported from skio-org/skio-plan-picker, rebuilt as a light-DOM
 * custom element so the theme's own CSS applies and the first paint is server
 * rendered (no layout shift, no third-party CDN, no Skio backend).
 *
 * Purity re-renders `.block-product__subscription-widget` from the Section
 * Rendering API on every variant change, so this element is destroyed and
 * recreated with fresh allocations. `restoreState` carries the shopper's
 * choice across that swap.
 */

/** @type {Map<string, {option: string, plan: string}>} */
const PICKER_STATE = new Map();

class PlanPicker extends HTMLElement {
  connectedCallback() {
    this.sellingPlanInput = this.querySelector(".plan-picker__selling-plan-input");
    this.outsideWrap = this.querySelector("[data-pp-outside]");
    this.stateKey = `${this.dataset.sectionId || ""}:${this.dataset.formId || ""}`;

    this.ensureSelection();
    this.restoreState();
    this.bind();
    this.syncAll();
    this.watchSubmitButton();
    this.renderSetupCheck();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.submitObserver?.disconnect();
  }

  /**
   * The theme owns the Add to cart label and rewrites it on its own schedule —
   * variant swatches replace the whole `.block-product__buttons` markup, and the
   * quantity widget reformats the amount. Rather than guess who runs last, watch
   * the form and put the plan price back whenever something overwrites it.
   */
  watchSubmitButton() {
    const form = this.productForm;
    if (!form || typeof MutationObserver === "undefined") return;

    this.submitObserver = new MutationObserver(() => {
      if (this.writingSubmitPrice) return;
      this.syncSubmitPrice(this.selectedOption, this.sellingPlanInput?.value);
    });
    this.submitObserver.observe(form, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  bind() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        if (target.matches("[data-pp-option]")) {
          this.syncAll();
        } else if (target.matches("[data-pp-plan], [data-pp-plan-select]")) {
          this.syncGroup(this.groupForControl(target));
          this.commit();
        }
      },
      { signal },
    );
  }

  /** Groups rendered in the DOM, one per selling plan group plus one-time. */
  get groups() {
    return Array.from(this.querySelectorAll("[data-pp-group]"));
  }

  get selectedOption() {
    return this.querySelector("[data-pp-option]:checked");
  }

  /**
   * A plan control can live inside its group (selector_position: inside) or in
   * the shared block below the groups (selector_position: outside).
   */
  groupForControl(control) {
    const inside = control.closest("[data-pp-group]");
    if (inside) return inside;

    const outsideGroup = control.closest("[data-pp-outside-group]");
    if (!outsideGroup) return null;
    return this.querySelector(
      `[data-pp-group][data-group-id="${outsideGroup.dataset.ppOutsideGroup}"]`,
    );
  }

  /**
   * The element holding a group's plan controls. With selector_position
   * "inside" that is the group itself; with "outside" it is the matching
   * block below the groups. Every plan lookup goes through here so the two
   * positions never drift apart.
   */
  planScopeFor(group) {
    if (!group) return null;
    if (group.querySelector("[data-pp-plan], [data-pp-plan-select]")) return group;
    if (!this.outsideWrap || !group.dataset.groupId) return null;
    return this.outsideWrap.querySelector(
      `[data-pp-outside-group="${group.dataset.groupId}"]`,
    );
  }

  /** The active plan control for a group, wherever it was rendered. */
  planControlFor(group) {
    const scope = this.planScopeFor(group);
    if (!scope) return null;
    return (
      scope.querySelector("[data-pp-plan]:checked") ||
      scope.querySelector("[data-pp-plan-select]")
    );
  }

  /** Reads price/discount data off whichever control type is in play. */
  planDataFrom(control) {
    if (!control) return null;
    if (control.tagName === "SELECT") {
      const option = control.selectedOptions[0];
      return option ? { value: option.value, data: option.dataset } : null;
    }
    return { value: control.value, data: control.dataset };
  }

  /**
   * Safety net: the frequency selector and perks live in .plan-picker__content,
   * which CSS only reveals for a :checked group. If no purchase option came back
   * checked from Liquid the whole picker would look inert, so select the first
   * available one rather than render a dead control.
   */
  ensureSelection() {
    if (this.selectedOption) return;
    const first = this.querySelector("[data-pp-option]:not(:disabled)");
    if (first) first.checked = true;
  }

  restoreState() {
    const saved = PICKER_STATE.get(this.stateKey);
    if (!saved) return;

    const option = this.querySelector(
      `[data-pp-option][value="${CSS.escape(saved.option)}"]:not(:disabled)`,
    );
    if (!option) return;
    option.checked = true;

    if (!saved.plan) return;
    const scope = this.planScopeFor(option.closest("[data-pp-group]"));
    if (!scope) return;

    const select = scope.querySelector("[data-pp-plan-select]");
    if (select) {
      if (Array.from(select.options).some((o) => o.value === saved.plan)) {
        select.value = saved.plan;
      }
      return;
    }
    const button = scope.querySelector(
      `[data-pp-plan][value="${CSS.escape(saved.plan)}"]`,
    );
    if (button) button.checked = true;
  }

  saveState(planId) {
    const option = this.selectedOption;
    if (!option) return;
    PICKER_STATE.set(this.stateKey, { option: option.value, plan: planId || "" });
  }

  /** Refreshes price, compare-at and discount inside one group. */
  syncGroup(group) {
    if (!group || group.dataset.purchaseType !== "subscription") return;

    const plan = this.planDataFrom(this.planControlFor(group));
    if (!plan) return;

    const discountValue = parseInt(plan.data.discount || "0", 10);

    const price = group.querySelector("[data-pp-price]");
    if (price && plan.data.price) price.textContent = plan.data.price;

    const compare = group.querySelector("[data-pp-compare]");
    if (compare) {
      if (plan.data.comparePrice) compare.textContent = plan.data.comparePrice;
      compare.hidden = !(discountValue > 0);
    }

    // Sale colour belongs to a price that is actually reduced. Without this a
    // 0%-off plan renders its ordinary price in the theme's sale red.
    const prices = group.querySelector("[data-pp-prices]");
    if (prices) {
      prices.classList.toggle("plan-picker__prices--discounted", discountValue > 0);
    }

    // Badge in the title and the ribbon above the card show the same saving
    if (plan.data.discountDisplay) {
      group
        .querySelectorAll("[data-pp-discount-value], [data-pp-ribbon-value]")
        .forEach((node) => {
          node.textContent = plan.data.discountDisplay;
        });
    }

    const discountWrap = group.querySelector("[data-pp-discount-wrap]");
    if (discountWrap) discountWrap.hidden = !(discountValue > 0);
  }

  /** Shows the selector block belonging to the selected group only. */
  syncOutsideSelectors() {
    if (!this.outsideWrap) return;

    const option = this.selectedOption;
    const isSubscription = option?.dataset.purchaseType === "subscription";
    const activeId = option?.dataset.groupId;

    this.outsideWrap.hidden = !isSubscription;
    this.outsideWrap
      .querySelectorAll("[data-pp-outside-group]")
      .forEach((node) => {
        node.hidden = node.dataset.ppOutsideGroup !== activeId;
      });
  }

  syncAll() {
    this.groups.forEach((group) => this.syncGroup(group));
    this.syncOutsideSelectors();
    this.commit();
  }

  /** Writes the hidden selling_plan input the product form submits. */
  commit() {
    if (!this.sellingPlanInput) return;

    const option = this.selectedOption;
    let planId = "";

    if (option && option.dataset.purchaseType === "subscription") {
      const plan = this.planDataFrom(
        this.planControlFor(option.closest("[data-pp-group]")),
      );
      planId = plan?.value || "";
    }

    if (this.sellingPlanInput.value !== planId) {
      this.sellingPlanInput.value = planId;
      this.sellingPlanInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.saveState(planId);
    this.syncSubmitPrice(option, planId);

    this.dispatchEvent(
      new CustomEvent("plan-picker:change", {
        bubbles: true,
        detail: {
          sellingPlanId: planId,
          purchaseType: option?.dataset.purchaseType || "one-time",
          formId: this.dataset.formId,
        },
      }),
    );
  }

  get productForm() {
    return (
      document.getElementById(this.dataset.formId || "") ||
      this.closest("form") ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  /**
   * Purity prints the variant price inside the Add to cart label
   * (`.total-price__detail[data-total-price]`, in cents) and multiplies it by
   * quantity. It has no idea a selling plan changed the price, so the button
   * would keep advertising the one-time amount. Rewrite the cents attribute and
   * let the theme's own quantity-input reformat it, so money formatting stays
   * the theme's job in every currency.
   */
  syncSubmitPrice(option, planId) {
    const form = this.productForm;
    if (!form) return;

    let cents = this.dataset.onetimeCents;
    let money = this.dataset.onetimeMoney;

    if (planId && option?.dataset.purchaseType === "subscription") {
      const plan = this.planDataFrom(
        this.planControlFor(option.closest("[data-pp-group]")),
      );
      if (plan?.data.priceCents) {
        cents = plan.data.priceCents;
        money = plan.data.price;
      }
    }
    if (!cents) return;

    const targets = [
      ...form.querySelectorAll(".total-price__detail"),
      ...document.querySelectorAll(
        ".sticky-sticky__buy-buttons .total-price__detail",
      ),
    ];
    if (!targets.length) return;

    // With quantity > 1 the label holds the multiplied amount, so only the cents
    // attribute is compared and the theme reformats the text.
    const quantityInput = form.querySelector("quantity-input");
    const qty = Number(quantityInput?.querySelector("input")?.value) || 1;
    const wantText = qty > 1 ? null : money;

    // Only touch the DOM when it actually differs, otherwise the observer above
    // would react to this write and loop.
    const stale = targets.filter(
      (node) =>
        node.getAttribute("data-total-price") !== String(cents) ||
        (wantText && node.textContent.trim() !== wantText),
    );
    if (!stale.length) return;

    this.writingSubmitPrice = true;
    stale.forEach((node) => {
      node.setAttribute("data-total-price", cents);
      if (wantText) node.textContent = wantText;
    });
    this.writingSubmitPrice = false;

    // Quantity maths stays the theme's job rather than reimplementing money_format.
    if (qty > 1 && quantityInput?.updateTotalPrice) {
      quantityInput.updateTotalPrice(qty);
    }
  }

  /** Theme-editor only diagnostic, mirroring Skio's setup mode. */
  renderSetupCheck() {
    const list = this.querySelector("[data-pp-setup-list]");
    if (!list) return;

    const form =
      document.getElementById(this.dataset.formId || "") ||
      this.closest("form") ||
      document.querySelector('form[action*="/cart/add"]');

    const checks = [
      ["Selling plan groups found", this.querySelectorAll('[data-purchase-type="subscription"]').length > 0],
      ["Product form found", Boolean(form)],
      ["Variant input found", Boolean(form?.querySelector('[name="id"]'))],
      ["selling_plan input wired to form", Boolean(this.sellingPlanInput)],
      ["A purchase option is selected", Boolean(this.selectedOption)],
      [
        "Only one selling_plan input on the page",
        document.querySelectorAll('input[name="selling_plan"]').length === 1,
      ],
    ];

    list.innerHTML = checks
      .map(([label, ok]) => `<li>${ok ? "✓" : "✗"} ${label}</li>`)
      .join("");
  }
}

if (!customElements.get("plan-picker")) {
  customElements.define("plan-picker", PlanPicker);
}
